import { useEffect, useState, useRef } from "react";
import { useStytchB2BClient } from "@stytch/react/b2b";
import { useNavigate } from "react-router-dom";
import useQueryStringParams from "../../hooks/useQueryStringParams";
import { trpc } from "../../trpc";
import ErrorDisplay from "../../components/auth/ErrorDisplay";
import LoadingDisplay from "../../components/auth/LoadingDisplay";
import SuccessDisplay from "../../components/auth/SuccessDisplay";
import OrganizationSelector from "../../components/auth/OrganizationSelector";
import type { PendingSignup, DiscoveredOrganization } from "../../components/auth/types";

const AuthenticateSignUp: React.FC = (): JSX.Element => {
  const stytch = useStytchB2BClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("Verificando correo electrónico...");
  const [success, setSuccess] = useState(false);
  const [signupData, setSignupData] = useState<PendingSignup | null>(null);
  const [discoveredOrgs, setDiscoveredOrgs] = useState<DiscoveredOrganization[]>([]);
  const [showOrgChoice, setShowOrgChoice] = useState(false);
  const [intermediateToken, setIntermediateToken] = useState<string>("");
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const hasInitiatedAuth = useRef(false);

  const { token, redirect } = useQueryStringParams(["token", "redirect"]);

  const createOrgMutation = trpc.auth.createOrganization.useMutation({
    onSuccess: async (data) => {
      // Clean up localStorage (keep authMode for AuthCallback to use)
      localStorage.removeItem("pendingSignup");

      setStatus("Organización creada exitosamente");
      setSuccess(true);

      // Navigate to auth callback with session token and optional redirect
      setTimeout(() => {
        const redirectParam = redirect ? `&redirect=${encodeURIComponent(redirect)}` : "";
        navigate(`/auth/callback?token=${encodeURIComponent(data.sessionToken)}${redirectParam}`);
      }, 2000);
    },
    onError: (error) => {
      console.error("Error creating organization:", error);
      
      // Check if it's a duplicate merchant error
      if (error.message?.includes("already exists")) {
        setError("Ya existe una organización con ese nombre. Por favor usa un nombre diferente o inicia sesión.");
      } else {
        setError("Error al crear la organización. Por favor intenta de nuevo.");
      }
    },
  });

  const exchangeSessionMutation = trpc.auth.exchangeSession.useMutation({
    onSuccess: async (data) => {
      // Clean up localStorage (keep authMode for AuthCallback to use)
      localStorage.removeItem("pendingSignup");

      // Navigate to auth callback with session token and optional redirect
      const redirectParam = redirect ? `&redirect=${encodeURIComponent(redirect)}` : "";
      navigate(`/auth/callback?token=${encodeURIComponent(data.sessionToken)}${redirectParam}`);
    },
    onError: (error) => {
      console.error("Error exchanging session:", error);
      setError("Error al unirse a la organización. Por favor intenta de nuevo.");
    },
  });

  const generateSlug = (companyName: string): string => {
    return companyName.toLowerCase().replace(/\s+/g, "").trim();
  };

  const handleCreateNewOrganization = () => {
    if (!signupData || !intermediateToken) return;

    const orgSlug = generateSlug(signupData.companyName);
    
    createOrgMutation.mutate({
      intermediateSessionToken: intermediateToken,
      organizationName: signupData.companyName,
      organizationSlug: orgSlug,
      merchantType: signupData.merchantType,
      memberName: signupData.leadName,
    });
  };

  const handleJoinOrganization = (org: DiscoveredOrganization) => {
    if (!intermediateToken) return;

    setSelectedOrgId(org.organization.organization_id);
    exchangeSessionMutation.mutate({
      intermediateSessionToken: intermediateToken,
      organizationId: org.organization.organization_id,
    });
  };

  useEffect(() => {
    const handleDiscoveryAuthentication = async () => {
      // Prevent duplicate execution
      if (hasInitiatedAuth.current) {
        return;
      }

      if (!token) {
        setError("Token de autenticación no encontrado");
        setTimeout(() => navigate("/signup"), 3000);
        return;
      }

      hasInitiatedAuth.current = true;

      try {
        // Get pending signup data from localStorage (if exists)
        const pendingSignupData = localStorage.getItem("pendingSignup");
        const pendingSignup: PendingSignup | null = pendingSignupData 
          ? JSON.parse(pendingSignupData) 
          : null;
        
        if (pendingSignup) {
          setSignupData(pendingSignup);
        }

        // Authenticate the discovery magic link
        const response = await stytch.magicLinks.discovery.authenticate({
          discovery_magic_links_token: token,
        });


        // Store the intermediate session token
        setIntermediateToken(response.intermediate_session_token);

        // Check if user has existing organizations
        if (response.discovered_organizations && response.discovered_organizations.length > 0) {
          // User has existing organizations
          if (pendingSignup) {
            // New signup with existing orgs - show choice to create new or join existing
            setDiscoveredOrgs(response.discovered_organizations);
            setShowOrgChoice(true);
            setStatus("Organizaciones encontradas");
          } else {
            // Login mode
            if (response.discovered_organizations.length === 1) {
              // Only one organization - auto-select and continue
              setStatus("Iniciando sesión...");
              exchangeSessionMutation.mutate({
                intermediateSessionToken: response.intermediate_session_token,
                organizationId: response.discovered_organizations[0].organization.organization_id,
              });
            } else {
              // Multiple organizations - show selector
              setDiscoveredOrgs(response.discovered_organizations);
              setShowOrgChoice(true);
              setStatus("Selecciona tu organización");
            }
          }
        } else {
          // No organizations found
          if (pendingSignup) {
            // New signup - create organization automatically
            setStatus("Creando nueva organización...");
            const orgSlug = generateSlug(pendingSignup.companyName);
            
            createOrgMutation.mutate({
              intermediateSessionToken: response.intermediate_session_token,
              organizationName: pendingSignup.companyName,
              organizationSlug: orgSlug,
              merchantType: pendingSignup.merchantType,
              memberName: pendingSignup.leadName,
            });
          } else {
            // Login mode but no organizations found
            setError("No se encontraron organizaciones asociadas a tu correo. Por favor regístrate primero.");
            setTimeout(() => navigate("/signup"), 3000);
          }
        }
      } catch (error) {
        console.error("Discovery authentication error:", error);
        setError("Token inválido o expirado. Por favor intenta de nuevo.");
        setTimeout(() => navigate("/signup"), 3000);
      }
    };

    handleDiscoveryAuthentication();
  }, [stytch, navigate, token]);

  if (error) {
    return (
      <ErrorDisplay 
        error={error} 
        onReturnToSignup={() => navigate("/signup")} 
      />
    );
  }

  if (showOrgChoice) {
    return (
      <OrganizationSelector
        discoveredOrgs={discoveredOrgs}
        signupData={signupData}
        selectedOrgId={selectedOrgId}
        isJoiningOrg={exchangeSessionMutation.isLoading}
        isCreatingOrg={createOrgMutation.isLoading}
        onJoinOrganization={handleJoinOrganization}
        onCreateNewOrganization={handleCreateNewOrganization}
      />
    );
  }

  if (success && signupData) {
    return <SuccessDisplay companyName={signupData.companyName} />;
  }

  return <LoadingDisplay status={status} />;
};

export default AuthenticateSignUp;

